import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../api";

const AuthContext = createContext(null);
const TOKEN_KEY = "trellure_token";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load session from token on mount
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setLoading(false));
  }, []);

  const refreshUsers = useCallback(async () => {
    try {
      const res = await api.get("/admin/users");
      setUsers(res.data);
    } catch (e) {
      setUsers([]);
    }
  }, []);

  useEffect(() => {
    if (user?.role === "admin") refreshUsers();
  }, [user, refreshUsers]);

  /**
   * The Worker already returns a usable sentence in `detail` — "Password must be
   * at least 4 characters", "A valid email is required". Passing it through is
   * the difference between a form that tells you what to fix and one that always
   * blames the email address. The fallback covers the network being down, which
   * is the one case the server cannot describe.
   */
  const detailOf = (e, fallback) =>
    e?.response?.data?.detail ||
    (e?.response ? fallback : "We could not reach Trellure. Check your connection and try again.");

  const login = async (email, password) => {
    try {
      const res = await api.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setUser(res.data.user);
      return { ok: true, user: res.data.user };
    } catch (e) {
      return { ok: false, error: detailOf(e, "Invalid credentials") };
    }
  };

  const register = async (name, email, password) => {
    try {
      const res = await api.post("/auth/register", { name, email, password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setUser(res.data.user);
      return { ok: true, user: res.data.user };
    } catch (e) {
      return { ok: false, error: detailOf(e, "We could not create that account.") };
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  /**
   * Takes a token minted elsewhere — the password reset flow signs the listener
   * straight in rather than sending them back to a form they just filled.
   */
  const adoptSession = useCallback((token, nextUser) => {
    localStorage.setItem(TOKEN_KEY, token);
    setUser(nextUser);
  }, []);

  /** Refetches the session after something outside this context changed it. */
  const refreshMe = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      return res.data;
    } catch (e) {
      return null;
    }
  }, []);

  /**
   * Returns whether it worked. It used to swallow the error, so the caller
   * showed "Purchase successful" whether the audiobook had been added or the
   * request had failed outright.
   */
  const addToLibrary = async (bookId) => {
    if (!user) return { ok: false, error: "Sign in first." };
    try {
      const res = await api.post("/me/library", { bookId });
      // The endpoint answers { ok, user }; older builds returned the user
      // directly, so accept both rather than blanking the session.
      setUser(res.data?.user ?? res.data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: detailOf(e, "We could not add it to your library.") };
    }
  };

  // Admins reach every audiobook without owning it: they curate the catalogue
  // and have to be able to open what they just published. The Worker enforces
  // the same rule, so this only keeps the UI from disagreeing with the API.
  const hasBook = (bookId) =>
    user?.role === "admin" || Boolean(user?.library?.includes(bookId));

  const toggleWishlist = async (bookId) => {
    if (!user) return { needsAuth: true };
    try {
      const res = await api.post("/me/wishlist", { bookId });
      setUser(res.data.user);
      return { added: res.data.added };
    } catch (e) {
      return {};
    }
  };

  const inWishlist = (bookId) => user?.wishlist?.includes(bookId);

  const grantAccess = async (userId, bookId) => {
    try {
      await api.post("/admin/entitlements", { userId, bookId });
      await refreshUsers();
      if (user?.id === userId) {
        const me = await api.get("/auth/me");
        setUser(me.data);
      }
    } catch (e) {}
  };

  const revokeAccess = async (userId, bookId) => {
    try {
      await api.delete(`/admin/entitlements?userId=${userId}&bookId=${bookId}`);
      await refreshUsers();
      if (user?.id === userId) {
        const me = await api.get("/auth/me");
        setUser(me.data);
      }
    } catch (e) {}
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        users,
        loading,
        isAdmin: user?.role === "admin",
        login,
        register,
        logout,
        refreshMe,
        adoptSession,
        addToLibrary,
        hasBook,
        toggleWishlist,
        inWishlist,
        grantAccess,
        revokeAccess,
        refreshUsers,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
